import { useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FileUp, FileDown, Database, Wand2, Users, Boxes, ShoppingCart, ShoppingBag } from "lucide-react";
import SmartImport from "@/components/app/SmartImport";
import { usePinPrompt } from "@/components/app/PinPrompt";

const TARGETS = [
  { k: "parties", label: "Parties", icon: Users },
  { k: "items", label: "Items / Stock", icon: Boxes },
  { k: "sale", label: "Sales", icon: ShoppingCart },
  { k: "purchase", label: "Purchases", icon: ShoppingBag },
];

function TallyImport() {
  const pinPrompt = usePinPrompt();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [vouchers, setVouchers] = useState(true);
  const [update, setUpdate] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const fl = e.target.files?.[0]; e.target.value = "";
    if (!fl) return;
    setFile(fl); setResult(null);
    const fd = new FormData(); fd.append("file", fl);
    try { const { data } = await api.post("/tally/preview", fd); setPreview(data); }
    catch (err) { setPreview(null); toast.error(err?.response?.data?.detail || "Could not read Tally file"); }
  };
  const run = async () => {
    await pinPrompt();
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/tally/import?include_vouchers=${vouchers}&update_existing=${update}`, fd); setResult(data); toast.success("Tally data imported"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-lg p-5" data-testid="tally-import-card">
      <div className="overline mb-1 flex items-center gap-2"><Database size={12} /> Import from Tally (ERP 9 / TallyPrime)</div>
      <p className="text-xs text-muted-foreground mb-3">In Tally: Gateway → Display → List of Accounts / Day Book → Alt+E Export → Format: <b>XML (Data Interchange)</b>. Upload the .xml file here. Debtor/creditor ledgers become parties, stock items become items, and Sales / Purchase / Credit / Debit Note vouchers become transactions.</p>
      <label className="flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg p-4 cursor-pointer hover:bg-slate-50" data-testid="tally-drop">
        <FileUp size={16} /> {file ? file.name : "Choose Tally XML file"}
        <input type="file" accept=".xml" className="hidden" onChange={pick} data-testid="tally-file" />
      </label>
      {preview && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            {["parties", "items", "vouchers"].map((k) => <div key={k} className="border border-border rounded p-2"><div className="text-2xl font-mono font-bold" data-testid={`tally-count-${k}`}>{preview[k]}</div><div className="text-xs uppercase text-slate-500">{k}</div></div>)}
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2"><Switch checked={vouchers} onCheckedChange={setVouchers} /><Label>Import vouchers</Label></div>
            <div className="flex items-center gap-2"><Switch checked={update} onCheckedChange={setUpdate} /><Label>Update existing masters</Label></div>
          </div>
          <Button className="btn-primary w-full" onClick={run} disabled={busy} data-testid="tally-import-btn">{busy ? "Importing…" : "Import into AccountifyPro"}</Button>
        </div>
      )}
      {result && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded p-3 text-sm" data-testid="tally-result">
          Parties: {result.parties.created} new · {result.parties.updated} updated · {result.parties.skipped} skipped<br />
          Items: {result.items.created} new · {result.items.updated} updated · {result.items.skipped} skipped<br />
          Vouchers: {result.vouchers.created} created · {result.vouchers.failed} failed
        </div>
      )}
    </div>
  );
}

function TallyExport() {
  const [what, setWhat] = useState("all");
  const download = async () => {
    try {
      const res = await api.get(`/tally/export?what=${what}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `accountifypro-tally-${what}.xml`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  return (
    <div className="bg-white border border-border rounded-lg p-5" data-testid="tally-export-card">
      <div className="overline mb-1 flex items-center gap-2"><FileDown size={12} /> Export to Tally / other ERP</div>
      <p className="text-xs text-muted-foreground mb-3">Download a Tally-compatible XML. In Tally: Gateway → Import → Masters / Vouchers → choose this file. Most ERPs (Busy, Marg, Zoho) accept Tally XML too.</p>
      <select className="w-full border border-border rounded-md h-9 px-2 bg-white mb-3" value={what} onChange={(e) => setWhat(e.target.value)} data-testid="tally-export-what">
        <option value="all">Masters + Vouchers</option>
        <option value="masters">Masters only (ledgers & stock items)</option>
        <option value="vouchers">Vouchers only (sales, purchases, notes)</option>
      </select>
      <Button variant="outline" className="w-full" onClick={download} data-testid="tally-export-btn"><FileDown size={14} /> Download Tally XML</Button>
      <div className="text-[10px] text-slate-400 mt-2 break-all">API: {API}/tally/export</div>
    </div>
  );
}

export default function DataImport() {
  const [target, setTarget] = useState(null);
  return (
    <div>
      <div className="overline">Data</div>
      <h1 className="text-3xl font-bold tracking-tight mb-1">Import & Tally Link</h1>
      <p className="text-sm text-muted-foreground mb-6">Bring data from Excel in any format, or move data both ways with Tally.</p>
      <div className="bg-white border border-border rounded-lg p-5 mb-6">
        <div className="overline mb-3 flex items-center gap-2"><Wand2 size={12} /> Smart Excel Import — any layout</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {TARGETS.map((t) => (
            <button key={t.k} onClick={() => setTarget(t.k)} data-testid={`import-target-${t.k}`}
              className="border border-border rounded-lg p-4 text-left hover:border-[#064E3B] hover:bg-emerald-50 transition-colors">
              <t.icon size={18} className="text-[#064E3B]" />
              <div className="font-semibold mt-2">{t.label}</div>
              <div className="text-xs text-slate-500">Upload & map columns</div>
            </button>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <TallyImport />
        <TallyExport />
      </div>
      {target && (
        <SmartImport target={["parties", "items"].includes(target) ? target : "invoices"} txnType={target}
          open={!!target} onClose={() => setTarget(null)} />
      )}
    </div>
  );
}
